import React, { Component } from 'react';
import { Stepper } from 'primereact/stepper';
import { StepperPanel } from 'primereact/stepperpanel';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { UtilService } from 'hay-core-react';
import Routerboard from '../../../models/Routerboard';
import RouterboardResultados from './RouterboardResultados';

const TENTATIVAS_TESTE_CONEXAO = 5;
const ESPERA_TESTE_CONEXAO_SEGUNDOS = 10;
const ESPERA_ENTRE_ETAPAS_SEGUNDOS = 5;

// Card Trello #103 - stepper vertical que roda, em sequência automática, a mesma bateria de
// comandos já disponível individualmente no SplitButton do rodapé (RouterboardFooter) - só
// reaproveita Routerboard.testarSsh/ativarApi/ativarSslLetsEncrypt/gerarScript/executarScript,
// sem nenhuma lógica de negócio nova no backend. Só a orquestração (sequência, espera entre
// etapas, retry do teste de conexão) é nova, e vive inteiramente aqui no frontend.
class RouterboardProvisionarPanel extends Component {

    state = { emExecucao: false, resultados: {} };

    stepperRef = React.createRef();
    // Guarda contra "setState em componente desmontado" - o usuário pode trocar de aba/fechar o
    // formulário no meio da sequência (que tem várias esperas de segundos via setTimeout).
    _cancelado = false;

    componentWillUnmount() {
        this._cancelado = true;
    }

    // Mesmo teste usado nas três etapas "Testar Conexão" (2.2/3.2/5.2) - o campo `seguro` da
    // routerboard já decide sozinho, do lado do backend (Routerboard.executarComandoRouterboard),
    // se o teste usa HTTP ou HTTPS - nada a decidir aqui.
    testarConexao = async () => {
        const res = await Routerboard.testarConexao(this.props.routerboard);
        return { status: !!res?.status, mensagem: RouterboardResultados.testarConexao(res) };
    }

    // `mensagem` de cada etapa usa exatamente o mesmo texto que apareceria no toast dessa mesma
    // ação via RouterboardFooter (RouterboardResultados, fonte única) - pedido do usuário depois
    // de testar o card #103: o resultado do stepper deve mostrar o output real do comando, não um
    // resumo genérico tipo "Sucesso"/"Erro".
    etapas = () => {
        const { routerboard, contexto } = this.props;
        const descricao = routerboard.descricao;
        return [
            {
                header: '1 - ' + UtilService.traduzir('main.routerboard.testarssh'),
                executar: async () => {
                    const res = await Routerboard.testarSsh(routerboard);
                    return { status: !!res?.status, mensagem: RouterboardResultados.testarSsh(res) };
                },
            },
            {
                header: '2.1 - ' + UtilService.traduzir('main.routerboard.ativarapi'),
                executar: async () => {
                    const res = await Routerboard.ativarApi(routerboard);
                    return { status: !!res?.status, mensagem: RouterboardResultados.ativarApi(res, descricao) };
                },
            },
            { header: '2.2 - ' + UtilService.traduzir('main.routerboard.testarconexao'), retry: true, executar: this.testarConexao },
            {
                header: '3.1 - ' + UtilService.traduzir('main.routerboard.ativarsslletsencrypt'),
                executar: async () => {
                    const res = await Routerboard.ativarSslLetsEncrypt(routerboard);
                    if (res?.status) {
                        // ativarSslLetsEncrypt marca seguro=true no banco (Routerboard.
                        // atualizarComAuditoria), mas `routerboard` aqui é uma cópia local
                        // (UtilService.extrairModel, capturada quando o formulário renderizou) -
                        // sem isso, as etapas seguintes (3.2 em diante) mandariam seguro=false
                        // desatualizado pro backend em cada chamada (RosRestService.conectar usa
                        // routerboard.seguro do corpo da requisição, não busca fresco do banco),
                        // inclusive gravando esse valor errado de novo no "Gerar Script"/"Salvar e
                        // Executar" seguintes. Mutação local + updateModelPath mantêm os dois
                        // sincronizados com o que o backend acabou de persistir.
                        routerboard.seguro = true;
                        contexto.updateModelPath('seguro.value', true);
                    }
                    return { status: !!res?.status, mensagem: RouterboardResultados.ativarSslLetsEncrypt(res, descricao) };
                },
            },
            { header: '3.2 - ' + UtilService.traduzir('main.routerboard.testarconexao'), retry: true, executar: this.testarConexao },
            {
                header: '4 - ' + UtilService.traduzir('main.routerboard.gerarscript'),
                executar: async () => {
                    const res = await Routerboard.gerarScript(routerboard);
                    if (res?.status) {
                        contexto.updateModelPath('script.value', res.data?.script);
                    }
                    return { status: !!res?.status, mensagem: RouterboardResultados.gerarScript(res, descricao) };
                },
            },
            {
                header: '5.1 - ' + UtilService.traduzir('main.routerboard.executarscript'),
                executar: async () => {
                    const salvou = await contexto.salvar(false);
                    if (!salvou) {
                        return { status: false, mensagem: UtilService.traduzir('main.routerboard.provisionar.etapa.falhasalvar') };
                    }
                    const res = await Routerboard.executarScript(routerboard);
                    return { status: !!res?.status, mensagem: RouterboardResultados.executarScript(res, descricao) };
                },
            },
            { header: '5.2 - ' + UtilService.traduzir('main.routerboard.testarconexao'), retry: true, executar: this.testarConexao },
        ];
    }

    aguardar = (segundos) => new Promise((resolve) => setTimeout(resolve, segundos * 1000));

    // Espera `segundos`, publicando a contagem regressiva em resultados[indice].contagem a cada
    // segundo - usado tanto na pausa entre etapas quanto entre tentativas do teste de conexão
    // (indicador visual pedido no card pra pausa entre etapas; reaproveitado na espera de retry
    // por ser a mesma necessidade de "o usuário precisa ver que algo está acontecendo").
    aguardarComContagem = async (indice, segundos) => {
        for (let restante = segundos; restante > 0; restante--) {
            if (this._cancelado) {return;}
            this.atualizarResultado(indice, { contagem: restante });
            await this.aguardar(1);
        }
    }

    atualizarResultado = (indice, patch) => {
        if (this._cancelado) {return;}
        this.setState((state) => ({
            resultados: { ...state.resultados, [indice]: { ...state.resultados[indice], ...patch } },
        }));
    }

    executarComRetry = async (etapa, indice) => {
        for (let tentativa = 1; tentativa <= TENTATIVAS_TESTE_CONEXAO; tentativa++) {
            if (this._cancelado) {return { status: false };}
            this.atualizarResultado(indice, { status: 'executando', tentativa, totalTentativas: TENTATIVAS_TESTE_CONEXAO, mensagem: null, contagem: null });
            const resultado = await etapa.executar();
            if (resultado.status || tentativa === TENTATIVAS_TESTE_CONEXAO) {
                return resultado;
            }
            this.atualizarResultado(indice, { status: 'aguardandotentativa', mensagem: resultado.mensagem });
            await this.aguardarComContagem(indice, ESPERA_TESTE_CONEXAO_SEGUNDOS);
        }
        return { status: false };
    }

    iniciar = async () => {
        const etapas = this.etapas();
        this._cancelado = false;
        this.setState({ emExecucao: true, resultados: {} });

        for (let indice = 0; indice < etapas.length; indice++) {
            if (this._cancelado) {return;}
            this.stepperRef.current?.setActiveStep(indice);
            this.atualizarResultado(indice, { status: 'executando', mensagem: null, tentativa: null, totalTentativas: null, contagem: null });

            const etapa = etapas[indice];
            const resultado = etapa.retry ? await this.executarComRetry(etapa, indice) : await etapa.executar();
            if (this._cancelado) {return;}

            if (!resultado.status) {
                this.atualizarResultado(indice, {
                    status: 'erro',
                    mensagem: resultado.mensagem || UtilService.traduzir('main.routerboard.provisionar.etapa.erro'),
                    tentativa: null,
                    totalTentativas: null,
                });
                this.setState({ emExecucao: false });
                return;
            }

            this.atualizarResultado(indice, { status: 'sucesso', mensagem: resultado.mensagem, tentativa: null, totalTentativas: null });

            if (indice < etapas.length - 1) {
                await this.aguardarComContagem(indice, ESPERA_ENTRE_ETAPAS_SEGUNDOS);
                if (this._cancelado) {return;}
                this.atualizarResultado(indice, { contagem: null });
                // Usuário pode ter navegado pra outra etapa durante a pausa - garante que a etapa
                // que acabou de rodar continua sendo a exibida antes de avançar (pedido do card).
                this.stepperRef.current?.setActiveStep(indice);
            }
        }

        this.setState({ emExecucao: false });
    }

    // Ícone de sucesso/falha no título da etapa (pedido do usuário, card #103) - SEM cor no texto
    // do título em si, só o ícone ao final. Implementação por caractere emoji (✅/❌), não por
    // JSX: `header` do StepperPanel precisa continuar sendo string simples.
    //
    // Duas restrições reais da versão instalada do PrimeReact (10.9.8) descartaram a alternativa
    // "ícone com círculo customizado via JSX":
    // 1. `header` é usado como key da lista de cabeçalhos internamente (getStepKey, ver
    //    node_modules/primereact/stepper/stepper.cjs.js) - um elemento React ali faria todas as
    //    etapas colidirem na mesma key, quebrando a navegação por clique.
    // 2. A API oficial pra customizar o cabeçalho por completo (`children={{header: fn}}`) esbarra
    //    num typo real do pacote (`tempate` em vez de `template` no caminho de renderização do
    //    conteúdo, StepperContent) - o conteúdo cai no fallback `props.stepperpanel`, que tenta
    //    renderizar o objeto `{header: fn}` cru como filho React e quebra com "Objects are not
    //    valid as a React child". Confirmado lendo o pacote compilado, não uma suposição.
    // ✅/❌ (emoji, cor já embutida no glyph) é o jeito seguro de atender o pedido dentro dessas
    // restrições - não é um `<i className="pi ...">` com círculo customizado, e isso foi
    // documentado no card pro usuário.
    headerTexto(texto, indice) {
        const { status } = this.state.resultados[indice] || {};
        if (status === 'sucesso') {return `${texto} ✅`;}
        if (status === 'erro') {return `${texto} ❌`;}
        return texto;
    }

    renderResultado(indice) {
        const { status, mensagem, tentativa, totalTentativas, contagem } = this.state.resultados[indice] || {};
        if (!status) {return null;}

        if (status === 'executando') {
            return (
                <div className="flex align-items-center justify-content-start gap-2 mt-2 w-full">
                    <ProgressSpinner style={{ width: '20px', height: '20px', margin: 0 }} strokeWidth="6" />
                    <span>{tentativa
                        ? `${UtilService.traduzir('main.routerboard.provisionar.status.tentativa')} ${tentativa}/${totalTentativas}`
                        : UtilService.traduzir('main.routerboard.provisionar.status.executando')}</span>
                </div>
            );
        }
        if (status === 'aguardandotentativa') {
            return (
                <div className="mt-2">
                    <Tag severity="warning" value={`${UtilService.traduzir('main.routerboard.provisionar.status.novatentativaem')} ${contagem ?? ESPERA_TESTE_CONEXAO_SEGUNDOS}s`} />
                    {mensagem && <div className="text-sm text-color-secondary mt-1">{mensagem}</div>}
                </div>
            );
        }
        if (status === 'sucesso') {
            return (
                <div className="mt-2">
                    <Tag severity="success" value={UtilService.traduzir('main.routerboard.provisionar.status.sucesso')} />
                    {mensagem && <div className="text-sm text-color-secondary mt-1">{mensagem}</div>}
                    {contagem != null && (
                        <div className="text-sm text-color-secondary mt-1">{`${UtilService.traduzir('main.routerboard.provisionar.status.avancandoem')} ${contagem}s...`}</div>
                    )}
                </div>
            );
        }
        if (status === 'erro') {
            return (
                <div className="mt-2">
                    <Tag severity="danger" value={UtilService.traduzir('main.routerboard.provisionar.status.erro')} />
                    {mensagem && <div className="text-sm text-color-secondary mt-1">{mensagem}</div>}
                </div>
            );
        }
        return null;
    }

    render() {
        const { routerboard } = this.props;
        const { emExecucao } = this.state;

        if (!routerboard?.id) {
            return <div className="p-3 text-color-secondary">{UtilService.traduzir('main.routerboard.provisionar.salveprimeiro')}</div>;
        }

        return (
            <div className="grid">
                <div className="col-12 flex justify-content-end mb-3">
                    <Button
                        label={UtilService.traduzir('main.routerboard.provisionar.iniciar')}
                        icon="pi pi-play"
                        disabled={emExecucao}
                        loading={emExecucao}
                        onClick={this.iniciar}
                    />
                </div>
                <div className="col-12">
                    <Stepper ref={this.stepperRef} orientation="vertical" linear={false}>
                        {this.etapas().map((etapa, indice) => (
                            <StepperPanel key={indice} header={this.headerTexto(etapa.header, indice)}>
                                {this.renderResultado(indice)}
                            </StepperPanel>
                        ))}
                    </Stepper>
                </div>
            </div>
        );
    }
}

export default RouterboardProvisionarPanel;
